/** Public settlement inhabitants and their analytic daily rhythm. Identity and
 * assignments are persisted; continuous transforms are derived from world
 * time and deterministic homestead paths, so residents require no tick wire. */
import { hashSeed } from "../rng.js";
import {
  DEFAULT_STYLE, EYE_COLORS, HAIR_COLORS, SKIN_COLORS, type CharacterStyle,
} from "../rpg.js";
import { npcWalkSpeedForId } from "../npcSchedule.js";
import { personGender } from "../worldgen/names.js";
import type { Vec3 } from "../homestead/types.js";
import type { HomesteadWalkPath } from "../homestead/rooms.js";
import type {
  SettlementFacilityV1, SettlementResidentTrait, SettlementResidentV1,
  SettlementStage, SettlementStateV1,
} from "./production.js";
import {
  LEGACY_SETTLEMENT_PROFESSIONS, SETTLEMENT_PROFESSIONS, SETTLEMENT_RESIDENT_TRAITS,
  SETTLEMENT_SPECIALTY_TOKENS,
  settlementFacilityFamily, settlementFacilityVariant,
} from "./production.js";
import { SETTLEMENT_BUILDING_VARIANTS, type SettlementBuildingFamily,
  type SettlementBuildingVariant } from "./model.js";

const PROFESSIONS = new Set<string>([...SETTLEMENT_PROFESSIONS, ...LEGACY_SETTLEMENT_PROFESSIONS]);
const TRAITS = new Set<string>(SETTLEMENT_RESIDENT_TRAITS);
const ROOM_FUNCTIONS = new Set(["dwelling", "commons", "mine", "farm", "salvage", "harvester",
  "workshop", "smithy", "kitchen", "exchange", "office", "storage"]);

export interface SettlementPublicResidentV1 extends SettlementResidentV1 {}

export interface SettlementPublicFacilityV1 {
  id: string;
  anchorPid: number;
  family: SettlementBuildingFamily;
  variant: SettlementBuildingVariant;
  roomFunction: SettlementFacilityV1["roomFunction"];
  grade: SettlementFacilityV1["grade"];
  working: boolean;
}

export interface SettlementPublicV1 {
  v: 1;
  beaconId: string;
  stage: SettlementStage;
  residents: SettlementPublicResidentV1[];
  facilities: SettlementPublicFacilityV1[];
}

export function settlementPublicState(state: SettlementStateV1): SettlementPublicV1 {
  return {
    v: 1,
    beaconId: state.beaconId,
    stage: state.stage,
    residents: state.residents.map((resident) => ({ ...resident })),
    facilities: state.facilities.map((facility) => ({
      id: facility.id,
      anchorPid: facility.anchorPid,
      family: settlementFacilityFamily(facility),
      variant: settlementFacilityVariant(facility),
      roomFunction: facility.roomFunction,
      grade: facility.grade,
      working: facility.queue.length > 0 && facility.queue[0]?.pauseReason === undefined,
    })),
  };
}

/** Defensive client wire guard. Public records intentionally contain no
 * storage, escrow, event, or owner information. */
export function validateSettlementPublicWire(raw: unknown): SettlementPublicV1 | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const stage = o.stage === undefined ? 0 : o.stage;
  if (o.v !== 1 || typeof o.beaconId !== "string" || o.beaconId.length > 200
    || !Number.isInteger(stage) || (stage as number) < 0 || (stage as number) > 3
    || !Array.isArray(o.residents) || o.residents.length > 32
    || !Array.isArray(o.facilities) || o.facilities.length > 32) return null;
  const residents: SettlementPublicResidentV1[] = [];
  for (const value of o.residents) {
    if (typeof value !== "object" || value === null) return null;
    const r = value as Record<string, unknown>;
    if (typeof r.id !== "string" || r.id.length > 160 || typeof r.name !== "string" || r.name.length > 80
      || !(r.profession === null || (typeof r.profession === "string" && PROFESSIONS.has(r.profession)))
      || !Number.isInteger(r.rank) || (r.rank as number) < 1 || (r.rank as number) > 5
      || !Number.isInteger(r.xp) || (r.xp as number) < 0
      || !(r.trait === undefined || (typeof r.trait === "string" && TRAITS.has(r.trait)))
      || !(r.specialty === undefined || (typeof r.specialty === "string" && SETTLEMENT_SPECIALTY_TOKENS.has(r.specialty)))
      || !(r.facilityId === undefined || (typeof r.facilityId === "string" && r.facilityId.length <= 200))
      || !(r.fedForDay === undefined || (Number.isInteger(r.fedForDay) && (r.fedForDay as number) >= 0))
      || !(r.mealHappinessPermille === undefined || (Number.isInteger(r.mealHappinessPermille)
        && (r.mealHappinessPermille as number) >= 0 && (r.mealHappinessPermille as number) <= 200))
      || !(r.homePid === null || (Number.isInteger(r.homePid) && (r.homePid as number) >= 0))
      || !(r.workplacePid === null || (Number.isInteger(r.workplacePid) && (r.workplacePid as number) >= 0))) return null;
    residents.push({ ...r } as unknown as SettlementPublicResidentV1);
  }
  const facilities: SettlementPublicFacilityV1[] = [];
  for (const value of o.facilities) {
    if (typeof value !== "object" || value === null) return null;
    const f = value as Record<string, unknown>;
    if (typeof f.id !== "string" || f.id.length > 160 || !Number.isInteger(f.anchorPid) || (f.anchorPid as number) < 0
      || typeof f.family !== "string" || !["generator", "refiner", "crafter", "trader", "admin", "living"].includes(f.family)
      || typeof f.variant !== "string" || !(SETTLEMENT_BUILDING_VARIANTS as readonly string[]).includes(f.variant)
      || typeof f.roomFunction !== "string" || !ROOM_FUNCTIONS.has(f.roomFunction)
      || !Number.isInteger(f.grade) || (f.grade as number) < 1 || (f.grade as number) > 5
      || typeof f.working !== "boolean") return null;
    facilities.push({ ...f } as unknown as SettlementPublicFacilityV1);
  }
  return { v: 1, beaconId: o.beaconId, stage: stage as SettlementStage, residents, facilities };
}

/** Same deterministic clothing/color idiom as region citizens. */
export function settlementResidentStyle(id: string): CharacterStyle {
  return {
    ...DEFAULT_STYLE,
    body: personGender(id) === "f" ? "female" : "male",
    suit: hashSeed(`${id}:style:suit`) & 0xffffff,
    accent: hashSeed(`${id}:style:accent`) & 0xffffff,
    skin: SKIN_COLORS[hashSeed(`${id}:style:skin`) % SKIN_COLORS.length],
    hairColor: HAIR_COLORS[hashSeed(`${id}:style:hairc`) % HAIR_COLORS.length],
    eyeColor: EYE_COLORS[hashSeed(`${id}:style:eyec`) % EYE_COLORS.length],
    torso: "drifter",
    legs: "drifter",
    feet: "drifter",
  };
}

export type SettlementResidentActivity = "sleep" | "home" | "walk" | "work" | "socialize";

export interface SettlementResidentCommonsRoute {
  commons: Vec3;
  workToCommons: HomesteadWalkPath;
  commonsToHome: HomesteadWalkPath;
}

export interface SettlementResidentPose {
  position: Vec3;
  activity: SettlementResidentActivity;
  moving: boolean;
  speed: number;
  /** Travel direction in planet-local coordinates. */
  forward: Vec3;
}

function pointAt(path: HomesteadWalkPath, distanceM: number, reverse: boolean): { position: Vec3; forward: Vec3 } {
  const d = Math.max(0, Math.min(path.totalM, reverse ? path.totalM - distanceM : distanceM));
  let segment = 1;
  while (segment + 1 < path.cumulativeM.length && path.cumulativeM[segment] < d) segment++;
  const a = path.points[segment - 1];
  const b = path.points[segment];
  const span = Math.max(1e-6, path.cumulativeM[segment] - path.cumulativeM[segment - 1]);
  const t = Math.max(0, Math.min(1, (d - path.cumulativeM[segment - 1]) / span));
  const direction: Vec3 = reverse
    ? [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
    : [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const l = Math.hypot(...direction) || 1;
  return {
    position: [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t],
    forward: [direction[0] / l, direction[1] / l, direction[2] / l],
  };
}

function routeDuration(path: HomesteadWalkPath | null, speed: number, dayPeriodS: number): number {
  return path && path.totalM > 0 ? Math.min(0.08, path.totalM / speed / Math.max(1, dayPeriodS)) : 0;
}

/** Trait changes the readable evening beat, not productivity. Careful
 * residents prefer their home routine; everyone else uses a valid Commons at
 * a slightly different time so the room does not move as one synchronized mob. */
function commonsDeparture(trait: SettlementResidentTrait | undefined, id: string): number | null {
  switch (trait) {
    case "sociable": return 0.75;
    case "curious": return 0.77;
    case "bold": return 0.79;
    case "steady": return 0.80;
    case "resourceful": return 0.81;
    case "careful": return null;
    default: return (hashSeed(`${id}:commons`) & 1) === 0 ? 0.80 : null;
  }
}

/** Pure daily pose. Departures are fixed to readable workday beats, while
 * travel duration comes from the actual voxel path and personal walk speed. */
export function settlementResidentPose(id: string, dayFrac: number, dayPeriodS: number,
  home: Vec3, work: Vec3, path: HomesteadWalkPath | null,
  commonsRoute?: SettlementResidentCommonsRoute | null,
  trait?: SettlementResidentTrait): SettlementResidentPose {
  const phaseShift = ((hashSeed(`${id}:clock`) % 1001) / 1000 - 0.5) * 0.025;
  let day = dayFrac + phaseShift;
  day -= Math.floor(day);
  const speed = npcWalkSpeedForId(id);
  const duration = routeDuration(path, speed, dayPeriodS);
  const stationary = (position: Vec3, activity: SettlementResidentActivity): SettlementResidentPose => ({
    position: [...position], activity, moving: false, speed: 0, forward: [0, 0, 1],
  });
  const travel = (depart: number, reverse: boolean): SettlementResidentPose | null => {
    if (!path || duration <= 0 || day < depart || day >= depart + duration) return null;
    const sampled = pointAt(path, (day - depart) * dayPeriodS * speed, reverse);
    return { ...sampled, activity: "walk", moving: true, speed };
  };
  const travelRoute = (route: HomesteadWalkPath, depart: number): SettlementResidentPose | null => {
    const routeTime = routeDuration(route, speed, dayPeriodS);
    if (routeTime <= 0 || day < depart || day >= depart + routeTime) return null;
    const sampled = pointAt(route, (day - depart) * dayPeriodS * speed, false);
    return { ...sampled, activity: "walk", moving: true, speed };
  };

  if (day < 0.24) return stationary(home, "sleep");
  const morning = travel(0.24, false); if (morning) return morning;
  if (day < 0.52) return stationary(path ? work : home, "work");
  const lunch = travel(0.52, true); if (lunch) return lunch;
  if (day < 0.60) return stationary(home, "home");
  const afternoon = travel(0.60, false); if (afternoon) return afternoon;
  const socialDepart = commonsRoute && path ? commonsDeparture(trait, id) : null;
  if (socialDepart !== null && commonsRoute) {
    if (day < socialDepart) return stationary(work, "work");
    const toCommons = travelRoute(commonsRoute.workToCommons, socialDepart);
    if (toCommons) return toCommons;
    if (day < 0.90) return stationary(commonsRoute.commons, "socialize");
    const toHome = travelRoute(commonsRoute.commonsToHome, 0.90);
    if (toHome) return toHome;
    if (day < 0.97) return stationary(home, "home");
    return stationary(home, "sleep");
  }
  if (day < 0.80) return stationary(path ? work : home, "work");
  const evening = travel(0.80, true); if (evening) return evening;
  if (day < 0.91) return stationary(home, "home");
  return stationary(home, "sleep");
}
